/**
 * Frame-aware emphasis hints for downstream UI surfaces (ResultsPane
 * column order + a one-line context banner at the top of the pane).
 *
 * gibbs-question-chooser v2 (2026-05-15). Returns a small, pure
 * descriptor object — UI components don't need to know about the
 * frame catalog directly; they just consume the hint.
 *
 * @module gibbs/question-chooser/frameEmphasis
 */

import { getActiveFrameId } from './activeFrameState';

/** Standard column ids that ResultsTable understands. */
export const COLUMN_IDS = Object.freeze({
    RANK: 'rank',
    SCORE: 'score',
    FLAGS: 'flags',
    INTERACTIONS: 'interactions',
});

const DEFAULT_COLUMN_ORDER = [
    COLUMN_IDS.RANK,
    COLUMN_IDS.SCORE,
    COLUMN_IDS.FLAGS,
    COLUMN_IDS.INTERACTIONS,
];

/**
 * @typedef {Object} FrameEmphasis
 * @property {string|null} frameId - Currently-active frame, or null.
 * @property {string[]} columnOrder - Ordered list of column ids.
 *   Rank always stays first.
 * @property {string|null} primaryEmphasis - The "winning" column to
 *   visually bold / highlight in the header. Null when no emphasis.
 * @property {string|null} banner - One-line context banner for the
 *   top of ResultsPane, or null when no frame is active.
 */

/** @type {FrameEmphasis} */
const DEFAULT_EMPHASIS = {
    frameId: null,
    columnOrder: DEFAULT_COLUMN_ORDER,
    primaryEmphasis: null,
    banner: null,
};

const FRAME_EMPHASIS_MAP = {
    'pick-best-hit': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.SCORE,
            COLUMN_IDS.FLAGS, COLUMN_IDS.INTERACTIONS],
        primaryEmphasis: COLUMN_IDS.SCORE,
        banner: 'Frame: picking the best hit — sorted by primary score; '
            + 'flag count is the tiebreak.',
    },
    'optimize-analog': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.FLAGS,
            COLUMN_IDS.INTERACTIONS, COLUMN_IDS.SCORE],
        primaryEmphasis: COLUMN_IDS.FLAGS,
        banner: 'Frame: triaging a single analog — flag count and '
            + 'interaction gaps first.',
    },
    'mitigate-toxicophore': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.FLAGS,
            COLUMN_IDS.SCORE, COLUMN_IDS.INTERACTIONS],
        primaryEmphasis: COLUMN_IDS.FLAGS,
        banner: 'Frame: mitigating a toxicophore — flag column comes '
            + 'first; score and interactions are secondary signals.',
    },
    'tune-interaction': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.INTERACTIONS,
            COLUMN_IDS.SCORE, COLUMN_IDS.FLAGS],
        primaryEmphasis: COLUMN_IDS.INTERACTIONS,
        banner: 'Frame: tuning an interaction — interaction count up '
            + 'front; score and flags are secondary.',
    },
    'compare-analogs': {
        // Compare-analogs uses the CompoundComparePane; ResultsPane
        // emphasis matches pick-best-hit so this frame is still
        // useful in the ranked-list context.
        columnOrder: DEFAULT_COLUMN_ORDER,
        primaryEmphasis: null,
        banner: 'Frame: comparing analogs — pick two and open the '
            + 'compare pane (type "compare compound A and B").',
    },
    'find-bioisostere': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.SCORE,
            COLUMN_IDS.INTERACTIONS, COLUMN_IDS.FLAGS],
        primaryEmphasis: COLUMN_IDS.INTERACTIONS,
        banner: 'Frame: bioisostere search — score + interaction '
            + 'preservation prioritized.',
    },
    'generate-new-compounds': {
        columnOrder: [COLUMN_IDS.RANK, COLUMN_IDS.SCORE,
            COLUMN_IDS.FLAGS, COLUMN_IDS.INTERACTIONS],
        primaryEmphasis: COLUMN_IDS.SCORE,
        banner: 'Frame: generating new compounds — fresh batches are '
            + 'usually screened first by score.',
    },
    'identify-active-site': {
        // No special column emphasis — active-site identification is
        // protein-side, not compound-list-side.
        columnOrder: DEFAULT_COLUMN_ORDER,
        primaryEmphasis: null,
        banner: 'Frame: active-site identification — needs a paper '
            + '(Theme I).',
    },
};

/**
 * Resolve the frame-aware emphasis for the current session. Reads
 * the active frame via gibbs/preferences.
 *
 * @returns {FrameEmphasis}
 */
export function getFrameEmphasis() {
    const id = getActiveFrameId();
    if (!id) return DEFAULT_EMPHASIS;
    const entry = FRAME_EMPHASIS_MAP[id];
    if (!entry) return { ...DEFAULT_EMPHASIS, frameId: id };
    return {
        frameId: id,
        columnOrder: entry.columnOrder || DEFAULT_COLUMN_ORDER,
        primaryEmphasis: entry.primaryEmphasis || null,
        banner: entry.banner || null,
    };
}

/**
 * Test helper — return the emphasis for a specific frame id without
 * touching the persisted active frame.
 *
 * @param {string|null} frameId
 * @returns {FrameEmphasis}
 */
export function getFrameEmphasisFor(frameId) {
    if (!frameId) return DEFAULT_EMPHASIS;
    const entry = FRAME_EMPHASIS_MAP[frameId];
    if (!entry) return { ...DEFAULT_EMPHASIS, frameId };
    return {
        frameId,
        columnOrder: entry.columnOrder || DEFAULT_COLUMN_ORDER,
        primaryEmphasis: entry.primaryEmphasis || null,
        banner: entry.banner || null,
    };
}
